import type { AxiosInstance } from 'axios';

import {
  addCalendarDays,
  calendarDayToLocalDate,
  dayInTimeZone,
  DEFAULT_TIME_ZONE,
  localDateToCalendarDay,
  zonedMidnight,
} from '../utils/timezone.ts';

/**
 * An all-day `time_slot` names calendar days, not instants, and the backend reads those days
 * on the vessel's clock: it takes the day each end falls on in the vessel's zone and stores
 * that day's 00:00 to 23:59:59 there (`daySpan` in the activity service). The app works in
 * local wall time (local midnight to local 23:59), so the slot is translated here, once, on
 * the way out and on the way in.
 *
 * Sent as is, a local day shifts: 26 Oct 00:00 in Budapest is 25 Oct 17:00 in El Salvador.
 * Read back as is, it shifts again: the vessel's 29 Oct 23:59:59 is 30 Oct in Budapest.
 */

type Slot = { start?: string; end?: string; all_day?: boolean };
type MapSlot = (slot: Slot, vesselId: string) => Slot;

const MINUTE = 60_000;

/** The local day `value` names, as its first or last minute on the vessel's clock. */
const toWire = (value: string | undefined, zone: string, last: boolean) => {
  if (!value) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = localDateToCalendarDay(date);
  return last
    ? new Date(zonedMidnight(addCalendarDays(day, 1), zone).getTime() - MINUTE).toISOString()
    : zonedMidnight(day, zone).toISOString();
};

/** The vessel's day an instant falls on, as local midnight or local 23:59 of that day. */
const fromWire = (value: string | undefined, zone: string, last: boolean) => {
  if (!value) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const local = calendarDayToLocalDate(dayInTimeZone(date, zone));
  if (last) local.setHours(23, 59);
  return local.toISOString();
};

const slotToWire = (slot: Slot, zone: string): Slot => ({
  ...slot,
  start: toWire(slot.start, zone, false),
  end: toWire(slot.end, zone, true),
});

const slotFromWire = (slot: Slot, zone: string): Slot => ({
  ...slot,
  start: fromWire(slot.start, zone, false),
  end: fromWire(slot.end, zone, true),
});

const isAllDaySlot = (value: unknown): value is Slot =>
  typeof value === 'object' && value !== null && (value as Slot).all_day === true;

// A slot belongs to the nearest `vessel_id` above it, so an event's sub-activities go by
// their parent; a body with none, like a create payload, goes by the request's vessel.
const mapSlots = (data: unknown, vesselId: string, map: MapSlot): unknown => {
  if (Array.isArray(data)) return data.map((item) => mapSlots(item, vesselId, map));
  if (typeof data !== 'object' || data === null || data instanceof FormData) return data;
  if (Object.getPrototypeOf(data) !== Object.prototype) return data;
  const record = data as Record<string, unknown>;
  const own =
    typeof record.vessel_id === 'string' && record.vessel_id ? record.vessel_id : vesselId;
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [
      key,
      key === 'time_slot' && isAllDaySlot(value) ? map(value, own) : mapSlots(value, own, map),
    ]),
  );
};

// ponytail: read once per vessel per page load, so a zone changed in the vessel's settings
// applies after a reload. Delete the vessel's entry on update if that ever matters.
const zones = new Map<string, Promise<string>>();

/** The vessel's zone, as the backend resolves it: UTC when the vessel has none. */
const zoneOf = (vessels: AxiosInstance, vesselId: string): Promise<string> => {
  if (!vesselId) return Promise.resolve(DEFAULT_TIME_ZONE);
  let zone = zones.get(vesselId);
  if (!zone) {
    zone = vessels
      .get<{ items: { timezone?: string }[] }>('', {
        params: { ids: [vesselId], page: 1, limit: 1 },
      })
      .then((response) => response.data.items[0]?.timezone || DEFAULT_TIME_ZONE)
      .catch((error: unknown) => {
        // Failing the request beats guessing a zone: a wrong day would be saved on the next edit.
        zones.delete(vesselId);
        throw error;
      });
    zones.set(vesselId, zone);
  }
  return zone;
};

const translate = async (
  data: unknown,
  vesselId: string,
  vessels: AxiosInstance,
  map: (slot: Slot, zone: string) => Slot,
) => {
  const ids = new Set<string>();
  mapSlots(data, vesselId, (slot, id) => {
    ids.add(id);
    return slot;
  });
  if (!ids.size) return data;
  const found = new Map(
    await Promise.all([...ids].map(async (id) => [id, await zoneOf(vessels, id)] as const)),
  );
  return mapSlots(data, vesselId, (slot, id) => map(slot, found.get(id) ?? DEFAULT_TIME_ZONE));
};

/** `vessels` is where the zone is looked up; the backend takes the vessel from X-Service-Id. */
export const translateAllDaySlots = (activity: AxiosInstance, vessels: AxiosInstance) => {
  // Request interceptors run last-added first, so the X-Service-Id one in api.ts has set the
  // header by the time this runs.
  activity.interceptors.request.use(async (config) => {
    const vesselId = String(config.headers['X-Service-Id'] ?? '');
    config.data = await translate(config.data, vesselId, vessels, slotToWire);
    return config;
  });
  activity.interceptors.response.use(async (response) => {
    const vesselId = String(response.config.headers['X-Service-Id'] ?? '');
    response.data = await translate(response.data, vesselId, vessels, slotFromWire);
    return response;
  });
};
